<template>
  <Teleport to="body">
    <!-- Overlay -->
    <Transition name="overlay-fade">
      <div
        v-if="isOpen"
        class="mini-cart-overlay"
        @click="$emit('close')"
        aria-hidden="true"
      ></div>
    </Transition>

    <!-- Drawer -->
    <Transition name="drawer-slide">
      <div
        v-if="isOpen"
        class="mini-cart-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="t('cart.yourCart')"
        tabindex="-1"
        ref="drawerRef"
      >
        <!-- Header -->
        <div class="mini-cart-header">
          <h5 class="mini-cart-title">{{ t('cart.yourCart') }}</h5>
          <button
            class="mini-cart-close"
            @click="$emit('close')"
            :aria-label="t('cart.closeCart')"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Items list -->
        <div class="mini-cart-body" v-if="cartItems.length > 0">
          <div
            v-for="item in cartItems"
            :key="item.lineId"
            class="mini-cart-item"
          >
            <!-- Thumbnail -->
            <div class="mini-cart-item-image">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
                class="item-thumb"
              />
              <div v-else class="item-thumb-placeholder">
                <i class="fas fa-box"></i>
              </div>
            </div>

            <!-- Info -->
            <div class="mini-cart-item-info">
              <p class="item-name" :title="item.name">{{ item.name }}</p>

              <!-- Variants -->
              <div
                v-if="item.variants && Object.keys(item.variants).length"
                class="item-variants"
              >
                <span
                  v-for="[vname, opt] in displayVariantEntries(item)"
                  :key="vname"
                  class="item-variant-badge"
                >
                  {{ vname }}: {{ opt }}
                </span>
              </div>

              <!-- Prices -->
              <div class="item-prices">
                <span
                  v-if="cartItemHasDiscount(item) && getCartItemOriginalPrice(item)"
                  class="item-original-price"
                >
                  {{ formatPrice(getCartItemOriginalPrice(item), item.currency) }}
                </span>
                <span class="item-price">
                  {{ formatPrice(item.price, item.currency) }}
                </span>
                <span v-if="cartItemHasDiscount(item)" class="item-discount-badge">
                  {{ getCartItemDiscountPercent(item) }}% OFF
                </span>
              </div>

              <!-- Controls row -->
              <div class="item-controls">
                <div class="quantity-controls">
                  <button
                    class="qty-btn"
                    type="button"
                    :aria-label="t('cart.qtyDecrease')"
                    @click="decrease(item)"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <span class="qty-value">{{ item.quantity }}</span>
                  <button
                    class="qty-btn"
                    type="button"
                    :aria-label="t('cart.qtyIncrease')"
                    @click="increase(item)"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>

                <span class="item-subtotal">
                  {{ formatPrice(item.price * item.quantity, item.currency) }}
                </span>

                <button
                  class="remove-btn"
                  type="button"
                  :aria-label="t('cart.removeItem')"
                  @click="remove(item.lineId)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="mini-cart-empty">
          <i class="fas fa-shopping-bag empty-icon"></i>
          <p class="empty-title">{{ t('cart.empty') }}</p>
          <a
            href="/productos"
            class="btn btn-outline-secondary btn-sm mt-3"
            @click="$emit('close')"
          >
            {{ t('cart.viewProducts') }}
          </a>
        </div>

        <!-- Footer -->
        <div v-if="cartItems.length > 0" class="mini-cart-footer">
          <div class="mini-cart-subtotal">
            <span class="subtotal-label">{{ t('cart.subtotal') }}</span>
            <span class="subtotal-value">{{ formatCartTotal }}</span>
          </div>
          <router-link
            to="/carrito"
            class="btn-checkout"
            @click="$emit('close')"
          >
            {{ t('cart.checkout') }}
          </router-link>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { formatProductPrice, convertAmount } from '@/helpers/currency';
import { useDisplayCurrency } from '@/composables/useDisplayCurrency';
import { useI18n } from '@/composables/useI18n';
import {
  cartItemHasDiscount,
  getCartItemDiscountPercent,
  getCartItemOriginalPrice,
} from '@/modules/cart/utils/cartItemPricing';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const drawerRef = ref(null);
const { displayCurrency } = useDisplayCurrency();
const { t } = useI18n();

const cartItems = computed(() => store.getters.cartItems);
const displayVariantEntries = (item) =>
  Object.entries(item.variants || {}).filter(([key]) => !String(key).startsWith('_'));

const formatPrice = (price, currency) => {
  return formatProductPrice(price, currency, displayCurrency.value);
};

const formatCartTotal = computed(() => {
  const total = cartItems.value.reduce((sum, item) => {
    const lineTotal = convertAmount(
      item.price * item.quantity,
      item.currency,
      displayCurrency.value,
    );
    return sum + lineTotal;
  }, 0);

  return formatProductPrice(total, displayCurrency.value, displayCurrency.value);
});

const increase = (item) => {
  store.dispatch('updateQuantity', { lineId: item.lineId, quantity: item.quantity + 1 });
};

const decrease = (item) => {
  if (item.quantity <= 1) {
    store.dispatch('removeFromCart', item.lineId);
  } else {
    store.dispatch('updateQuantity', { lineId: item.lineId, quantity: item.quantity - 1 });
  }
};

const remove = (lineId) => {
  store.dispatch('removeFromCart', lineId);
};

const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.isOpen) emit('close');
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

watch(
  () => props.isOpen,
  async (val) => {
    if (val) {
      document.body.style.overflow = 'hidden';
      await nextTick();
      drawerRef.value?.focus();
    } else {
      document.body.style.overflow = '';
    }
  }
);
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────────── */
.mini-cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
}

/* ── Drawer panel ─────────────────────────────────────── */
.mini-cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  z-index: 1056;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 28px rgba(0, 0, 0, 0.14);
  outline: none;
  font-family: 'Host Grotesk', sans-serif;
  font-weight: 300;
}

/* ── Transitions ──────────────────────────────────────── */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

/* ── Header ───────────────────────────────────────────── */
.mini-cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.mini-cart-title {
  font-size: 0.78rem;
  font-weight: 300;
  margin: 0;
  color: #1a1a1a;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mini-cart-close {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: #666;
  padding: 0.35rem;
  line-height: 1;
  border-radius: 0;
  transition: background 0.15s, color 0.15s;
}

.mini-cart-close:hover {
  background: #f5f5f5;
  color: #1a1a1a;
}

/* ── Body (scrollable) ────────────────────────────────── */
.mini-cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0;
}

/* ── Item ─────────────────────────────────────────────── */
.mini-cart-item {
  display: flex;
  gap: 0.875rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f5f5f5;
}

.mini-cart-item:last-child {
  border-bottom: none;
}

.mini-cart-item-image {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 0;
  overflow: hidden;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-thumb-placeholder {
  color: #ccc;
  font-size: 1.4rem;
}

.mini-cart-item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.875rem;
  font-weight: 300;
  color: #1a1a1a;
  margin: 0 0 0.25rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
}

.item-variant-badge {
  font-size: 0.7rem;
  font-weight: 300;
  color: #555;
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 0;
}

.item-prices {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.item-original-price {
  font-size: 0.75rem;
  color: #aaa;
  text-decoration: line-through;
}

.item-price {
  font-size: 0.875rem;
  font-weight: 300;
  color: #1a1a1a;
}

.item-discount-badge {
  font-size: 0.68rem;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 5px;
  border-radius: 0;
  font-weight: 300;
}

/* ── Controls ─────────────────────────────────────────── */
.item-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.quantity-controls {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border: 1px solid #e0e0e0;
  border-radius: 0;
  overflow: hidden;
}

.qty-btn {
  background: transparent;
  border: none;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  font-size: 0.65rem;
  transition: background 0.15s;
}

.qty-btn:hover {
  background: #f5f5f5;
}

.qty-value {
  width: 16px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 300;
  color: #1a1a1a;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  line-height:  16px;
}

.item-subtotal {
  font-size: 0.82rem;
  font-weight: 300;
  color: #1a1a1a;
  margin-left: auto;
  white-space: nowrap;
}

.remove-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #ccc;
  font-size: 0.82rem;
  padding: 0.2rem;
  transition: color 0.2s;
  line-height: 1;
}

.remove-btn:hover {
  color: #e53935;
}

/* ── Empty state ──────────────────────────────────────── */
.mini-cart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: #ddd;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 0.875rem;
  font-weight: 300;
  color: var(--black-color) !important;
  margin: 0 0 0.35rem;
  letter-spacing: 0.08em;
}

.empty-sub {
  font-size: 0.85rem;
  color: var(--black-color-2) !important;
  margin: 0;
}

/* ── Footer ───────────────────────────────────────────── */
.mini-cart-footer {
  border-top: 1px solid #f0f0f0;
  padding: 1.25rem 1.5rem;
  flex-shrink: 0;
  background: #fff;
}

.mini-cart-subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.subtotal-label {
  font-size: 0.78rem;
  font-weight: 300;
  color: #555;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.subtotal-value {
  font-size: 0.875rem;
  font-weight: 300;
  color: #1a1a1a;
}

.btn-checkout {
  display: block;
  width: 100%;
  background-color: #1a1a1a;
  color: #fff !important;
  border: none;
  padding: 0.78rem 1rem;
  border-radius: 0;
  font-weight: 300;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  text-align: center;
  transition: background 0.2s, transform 0.1s;
  cursor: pointer;
}

.btn-checkout:hover {
  background-color: #333;
}

.btn-checkout:active {
  transform: scale(0.98);
}

.mini-cart-empty :deep(.btn) {
  border-radius: 0;
  font-family: 'Host Grotesk', sans-serif;
  font-weight: 300;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.72rem;
}

/* ── Responsive ───────────────────────────────────────── */
@media (max-width: 768px) {
  .mini-cart-drawer {
    width: 85%;
  }

  .mini-cart-item-image {
    width: 72px;
    height: 72px;
  }

  .qty-btn {
    width: 28px;
    height: 28px;
  }

  .qty-value {
    width: 28px;
    line-height: 28px;
  }
}

@media (max-width: 400px) {
  .mini-cart-drawer {
    width: 100%;
  }
}
</style>
